import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { OEM_BRANDS } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field, SelectField } from "@/components/common/Field";
import { Loader2 } from "lucide-react";

const EMPTY = {
  name: "", oem_brand: "", contact_person: "", mobile: "", city: "", location: "", remarks: "",
};

export const DealershipFormDialog = ({ open, onOpenChange, onSaved, initial }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial?.id);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      const res = editing
        ? await api.put(`/dealerships/${initial.id}`, payload)
        : await api.post("/dealerships", payload);
      toast.success(editing ? "Dealership updated" : "Dealership created");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save dealership");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Dealership" : "New Dealership"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Dealership Name">
            <Input className="h-11" placeholder="e.g. ABC Hyundai" value={form.name} onChange={set("name")} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="OEM / Brand">
              <SelectField value={form.oem_brand} onChange={set("oem_brand")} options={OEM_BRANDS} placeholder="Select brand" />
            </Field>
            <Field label="City">
              <Input className="h-11" placeholder="City" value={form.city} onChange={set("city")} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact Person">
              <Input className="h-11" placeholder="Name" value={form.contact_person} onChange={set("contact_person")} />
            </Field>
            <Field label="Mobile Number">
              <Input className="h-11" type="tel" inputMode="tel" placeholder="Mobile" value={form.mobile} onChange={set("mobile")} />
            </Field>
          </div>
          <Field label="Location / Address">
            <Textarea rows={2} placeholder="Area, landmark, address" value={form.location} onChange={set("location")} />
          </Field>
          <Field label="Remarks">
            <Textarea rows={2} placeholder="Optional notes" value={form.remarks} onChange={set("remarks")} />
          </Field>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button variant="brand" onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? "Save Changes" : "Create Dealership"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
